// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCommunityStats, getAuditLog } from '../../lib/db';
import { db } from '../../lib/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { PageBack } from '../../components/shared/PageBack';
import { useAuth } from '../../lib/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, isTownAdmin, townAdminScope } = useAuth();
  const [stats, setStats]   = useState(null);
  const [audit, setAudit]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all'); // 'all' | '30' | '7'
  const [pendingCount, setPendingCount] = useState(0);

  const loadCategoryStats = async (filter) => {
    const cutoff = new Date();
    if (filter === '30') cutoff.setDate(cutoff.getDate() - 30);
    if (filter === '7') cutoff.setDate(cutoff.getDate() - 7);

    const eventsSnap = await getDocs(
      filter === 'all'
        ? query(collection(db, 'events'), where('status', '==', 'published'))
        : query(collection(db, 'events'), where('status', '==', 'published'), where('createdAt', '>=', cutoff))
    );

    const counts = {};
    eventsSnap.docs.forEach(d => {
      const cat = d.data().category || 'Community';
      counts[cat] = (counts[cat] || 0) + 1;
    });

    const sorted = Object.entries(counts)
      .map(([cat, count]) => ({ cat, count }))
      .sort((a, b) => b.count - a.count);

    setCategoryStats(sorted);
  };

  const [pendingByType, setPendingByType] = useState({ events: 0, skills: 0, services: 0 });

  const loadPendingCount = async () => {
    const [eventsSnap, skillsSnap, servicesSnap] = await Promise.all([
      getDocs(query(collection(db, 'events'), where('status', '==', 'pending_review'))),
      getDocs(query(collection(db, 'skills'), where('status', '==', 'pending_review'))),
      getDocs(query(collection(db, 'services'), where('status', '==', 'pending_review'))),
    ]);
    setPendingByType({ events: eventsSnap.size, skills: skillsSnap.size, services: servicesSnap.size });
    setPendingCount(eventsSnap.size + skillsSnap.size + servicesSnap.size);
  };

  useEffect(() => {
    Promise.all([getCommunityStats(), getAuditLog(10)]).then(([s, a]) => {
      setStats(s);
      setAudit(a);
      setLoading(false);
    });
    loadCategoryStats('all');
    loadPendingCount();
  }, []);

  const sections = [
    { label: '🗺️ Counties',        sub: 'Add a county before adding towns under it',      path: '/admin/counties', fullAdminOnly: true },
    { label: '🏘️ Towns',           sub: 'Add & manage towns, go-live dates',              path: '/admin/towns', fullAdminOnly: true },
    { label: '👥 Users',           sub: 'Manage roles · grant organiser access',           path: '/admin/users', fullAdminOnly: true },
    { label: '🙋 Town Admin Applications', sub: 'Review requests to help manage a town',   path: '/admin/town-admin-applications', fullAdminOnly: true },
    { label: '📅 Events',          sub: 'Moderate all events · pending review queue',       path: '/admin/events' },
    { label: '💰 Sponsors',        sub: 'Founding Partners · Gold · Community Supporters', path: '/admin/sponsors', fullAdminOnly: true },
    { label: '📊 Sponsor Analytics', sub: 'Impressions, clicks & reach by sponsor',         path: '/admin/analytics', fullAdminOnly: true },
    { label: '📈 Impact Report',   sub: 'Shareable stats for sponsors, LEADER, grants',    path: '/admin/impact-report', fullAdminOnly: true },
    { label: '🚀 Town Launch Checklist', sub: 'Repeatable go-live steps per town',         path: '/admin/launch-checklist' },
    { label: '🤖 Event Scanner',   sub: 'Kill switch · scan logs · pending review',        path: '/admin/scanner', fullAdminOnly: true },
    { label: '🏘️ Town Services',   sub: 'Directory listings · featured tiers',             path: '/admin/services' },
    { label: '✨ Skill Share',      sub: 'Approve, hide, delete listings',              path: '/admin/skills' },
    { label: '🤝 Community',        sub: 'Noticeboard and Local Meet-ups — approve, hide', path: '/admin/community' },
    { label: '🏷️ Deals',            sub: 'Approve, hide, delete time-limited offers',   path: '/admin/deals' },
    { label: '📋 Audit Log',       sub: 'All admin actions logged',                        path: '/admin/audit', fullAdminOnly: true },
  ].filter(section => !section.fullAdminOnly || isAdmin);

  const fmtDate = (ts) => {
    if (!ts) return '—';
    try { return format(ts?.toDate ? ts.toDate() : new Date(ts), 'd MMM · HH:mm'); }
    catch { return '—'; }
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <PageBack fallbackPath="/profile" />
      <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>
        {isTownAdmin ? 'Town Admin' : 'Admin'}
      </h1>
      <p style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 500, marginBottom: 20 }}>
        {isTownAdmin ? 'Managing your local town' : 'Gather platform management'}
      </p>
      {pendingCount > 0 && (
        <button onClick={() => {
          if (pendingByType.events > 0) navigate('/admin/events');
          else if (pendingByType.services > 0) navigate('/admin/services');
          else navigate('/admin/skills');
        }} style={{ display: 'block', width: '100%', textAlign: 'left', background: '#FFF3CD', color: '#8A6D1D', border: 'none', borderRadius: 14, padding: '12px 16px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', marginBottom: 16, cursor: 'pointer' }}>
          🔔 {pendingCount} item{pendingCount !== 1 ? 's' : ''} waiting for review — tap to check
          {[pendingByType.events > 0 && 'Events', pendingByType.services > 0 && 'Services', pendingByType.skills > 0 && 'Skill Share'].filter(Boolean).length > 1 && (
            <span style={{ display: 'block', fontWeight: 500, fontSize: 11, marginTop: 2 }}>
              Spread across: {[pendingByType.events > 0 && 'Events', pendingByType.services > 0 && 'Services', pendingByType.skills > 0 && 'Skill Share'].filter(Boolean).join(', ')}
            </span>
          )}
        </button>
      )}

      {/* Community Impact Stats */}
      {stats && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
            Community Impact
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 20 }}>
            {[
              ['📅', stats.totalEvents,       'Events'],
              ['👥', stats.totalUsers,         'Users'],
              ['🏘️', stats.liveTowns,          'Towns live'],
              ['🙋', stats.volunteerOpps,      'Volunteer opps'],
              ['❤️', stats.totalInteractions,  'Interactions'],
            ].map(([icon, val, lbl]) => (
              <div key={lbl} style={{ background: 'var(--white)', borderRadius: 14, padding: '12px 10px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ fontSize: 20 }}>{icon}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink)', marginTop: 3 }}>{val ?? '—'}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{lbl}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Nav sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {sections.map(s => (
          <button key={s.path} onClick={() => navigate(s.path)}
            style={{ background: 'var(--white)', border: 'none', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', fontFamily: 'Nunito', textAlign: 'left', minHeight: 60 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 }}>{s.label}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{s.sub}</div>
            </div>
            <span style={{ color: 'var(--border)', fontSize: 20 }}>›</span>
          </button>
        ))}
      </div>

      {/* Category breakdown */}
      {categoryStats.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Events by category</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['all', 'All time'], ['30', '30 days'], ['7', '7 days']].map(([val, lbl]) => (
                <button key={val} onClick={() => { setTimeFilter(val); loadCategoryStats(val); }}
                  style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 100, border: 'none', cursor: 'pointer', fontFamily: 'Nunito',
                    background: timeFilter === val ? 'var(--sage-deep)' : 'var(--sage-pale)',
                    color: timeFilter === val ? 'white' : 'var(--sage-dark)' }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 16px', marginBottom: 20, boxShadow: 'var(--shadow-sm)' }}>
            {categoryStats.map(({ cat, count }) => {
              const max = categoryStats[0].count;
              const pct = Math.round((count / max) * 100);
              return (
                <div key={cat} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{cat}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sage-deep)' }}>{count}</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--sage-pale)', borderRadius: 100 }}>
                    <div style={{ height: 6, background: 'var(--sage-deep)', borderRadius: 100, width: `${pct}%`, transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Recent audit log */}
      {audit.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
            Recent activity
          </div>
          {audit.slice(0, 5).map(entry => (
            <div key={entry.id} style={{ background: 'var(--white)', borderRadius: 12, padding: '10px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{entry.action}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{fmtDate(entry.timestamp)}</div>
            </div>
          ))}
        </>
      )}

      <button onClick={() => navigate('/')} style={{ marginTop: 16, width: '100%', background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 14, padding: 14, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
        ← Back to app
      </button>
    </div>
  );
};

export default AdminDashboard;
